import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import { AxiosError } from 'axios';
import {
  type Collection,
  type UpdateCollectionPayload,
} from '../types';
import { collectionApi } from '../api';
import { collectionKeys } from './collection.keys';

// Mutação de atualização
export function useUpdateCollection(
  options?: UseMutationOptions<Collection, Error, { id: string; data: UpdateCollectionPayload }>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['collections', 'update'],
    mutationFn: ({ id, data }: { id: string; data: UpdateCollectionPayload }) => collectionApi.update(id, data),
    ...mutationOptions,
    onSuccess: (collection, variables, onMutateResult, context) => {
      toast({
        title: 'Coleção atualizada com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.all });
      providedOnSuccess?.(collection, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      const err = error as AxiosError<{ message: string }>;

      toast({
        title: 'Erro ao atualizar coleção',
        description: err?.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  const updateCollection = (id: string, data: UpdateCollectionPayload) => mutation.mutateAsync({ id, data });
  return {
    updateCollection,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
